import React, { useEffect, useRef, useState, useMemo } from 'react';
import * as d3 from 'd3';
import { TransformationRoadmapChartProps } from '../../types';

// Helper function to get month name
const getMonthName = (month: number): string => {
  const monthNames = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
  ];
  return monthNames[month - 1] || '';
};

// Helper function to get month and year from absolute month number
const getMonthFromAbsolute = (absoluteMonth: number): { month: number; year: number } => {
  const year = Math.floor((absoluteMonth - 1) / 12);
  const month = ((absoluteMonth - 1) % 12) + 1;
  return { month, year };
};

// Chart layout constants (shared between useEffect and render)
const QUARTER_WIDTH = 450;
const MARGIN_LEFT = 60;
const MARGIN_RIGHT = 60;
const TIMELINE_PADDING_LEFT = 32;
const TIMELINE_PADDING_RIGHT = 32;

const TransformationRoadmapChart: React.FC<TransformationRoadmapChartProps> = ({
  width = 1200,
  height = 500,
  data,
  showTooltips = true,
  showLegends = true,
  interactive = true,
  onTaskClick,
  onMilestoneClick
}) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [zoomScale, setZoomScale] = useState(1);
  const [tooltip, setTooltip] = useState<{ show: boolean; content: string; x: number; y: number }>({
    show: false,
    content: '',
    x: 0,
    y: 0
  });

  // Calculate dynamic timeline based on actual project data
  const timelineData = useMemo(() => {
    if (!data?.tasks || data.tasks.length === 0) {
      const now = new Date();
      const currentYear = now.getFullYear();
      const currentMonth = now.getMonth() + 1;
      const startAbsoluteMonth = currentYear * 12 + currentMonth;
      const endAbsoluteMonth = currentYear * 12 + 12;
      
      return {
        startAbsoluteMonth,
        endAbsoluteMonth,
        quarters: [],
        years: [currentYear]
      };
    }

    // Find the minimum and maximum absolute month numbers from all tasks
    let earliestStartAbsoluteMonth = Infinity;
    let latestEndAbsoluteMonth = -Infinity;
    
    data.tasks.forEach(task => {
      if (task.startMonth) {
        earliestStartAbsoluteMonth = Math.min(earliestStartAbsoluteMonth, task.startMonth);
      }
      // Only consider tasks that have an end month (not undefined)
      if (task.endMonth) {
        latestEndAbsoluteMonth = Math.max(latestEndAbsoluteMonth, task.endMonth);
      }
    });
    
    // Default to current year if no dates
    if (earliestStartAbsoluteMonth === Infinity) {
      const now = new Date();
      const currentYear = now.getFullYear();
      earliestStartAbsoluteMonth = currentYear * 12 + 1;
    }
    
    // Calculate the earliest start quarter
    const earliestStartYear = Math.floor((earliestStartAbsoluteMonth - 1) / 12);
    const earliestStartMonthNum = ((earliestStartAbsoluteMonth - 1) % 12) + 1;
    const earliestStartQuarter = Math.floor((earliestStartMonthNum - 1) / 3);
    
    // Calculate the latest end quarter (from projects that have end months)
    let latestEndQuarter = earliestStartQuarter;
    let latestEndYear = earliestStartYear;
    
    if (latestEndAbsoluteMonth !== -Infinity) {
      latestEndYear = Math.floor((latestEndAbsoluteMonth - 1) / 12);
      const latestEndMonthNum = ((latestEndAbsoluteMonth - 1) % 12) + 1;
      latestEndQuarter = Math.floor((latestEndMonthNum - 1) / 3);
    } else {
      // If no projects have end months, default to 6 quarters from start
      latestEndQuarter = earliestStartQuarter + 5;
      latestEndYear = earliestStartYear;
      while (latestEndQuarter > 3) {
        latestEndQuarter -= 4;
        latestEndYear += 1;
      }
    }
    
    // Calculate how many quarters we need (from earliest start to latest end)
    const quartersFromStart = (latestEndYear - earliestStartYear) * 4 + (latestEndQuarter - earliestStartQuarter) + 1;
    
    // Cap at maximum 6 quarters
    const totalQuartersToShow = Math.min(quartersFromStart, 6);
    
    // Start from the earliest project's starting quarter
    const displayStartQuarter = earliestStartQuarter;
    const displayStartYear = earliestStartYear;
    
    // Generate all quarters in the display range (up to 6 quarters)
    const quarters: Array<{ quarter: number; year: number; startAbsoluteMonth: number; endAbsoluteMonth: number; months: Array<{ month: number; year: number; absoluteMonth: number }> }> = [];
    const years: number[] = [];
    
    let currentYear = displayStartYear;
    let currentQuarter = displayStartQuarter;
    
    for (let i = 0; i < totalQuartersToShow; i++) {
      if (currentQuarter > 3) {
        currentQuarter = 0;
        currentYear += 1;
      }
      
      if (!years.includes(currentYear)) {
        years.push(currentYear);
      }
      
      const quarterStartMonth = currentYear * 12 + currentQuarter * 3 + 1;
      const quarterEndMonth = currentYear * 12 + currentQuarter * 3 + 3;
      
      // Generate months for this quarter
      const months: Array<{ month: number; year: number; absoluteMonth: number }> = [];
      for (let month = quarterStartMonth; month <= quarterEndMonth; month++) {
        const monthYear = Math.floor((month - 1) / 12);
        const monthNum = ((month - 1) % 12) + 1;
        months.push({
          month: monthNum,
          year: monthYear,
          absoluteMonth: month
        });
      }
      
      quarters.push({
        quarter: currentQuarter + 1,
        year: currentYear,
        startAbsoluteMonth: quarterStartMonth,
        endAbsoluteMonth: quarterEndMonth,
        months
      });
      
      currentQuarter += 1;
    }
    
    // Calculate the actual start and end absolute months for the display range
    const displayStartAbsoluteMonth = quarters[0].startAbsoluteMonth;
    const displayEndAbsoluteMonth = quarters[quarters.length - 1].endAbsoluteMonth;
    
    return {
      startAbsoluteMonth: displayStartAbsoluteMonth,
      endAbsoluteMonth: displayEndAbsoluteMonth,
      quarters,
      years
    };
  }, [data]);

  const sortedTasks = useMemo(() => {
    if (!data?.tasks) return [];

    return [...data.tasks].sort((a, b) => {
      const startDiff = (a.startMonth ?? Number.MAX_SAFE_INTEGER) - (b.startMonth ?? Number.MAX_SAFE_INTEGER);
      if (startDiff !== 0) return startDiff;

      const endDiff = (a.endMonth ?? Number.MAX_SAFE_INTEGER) - (b.endMonth ?? Number.MAX_SAFE_INTEGER);
      if (endDiff !== 0) return endDiff;

      return a.name.localeCompare(b.name);
    });
  }, [data?.tasks]);

  useEffect(() => {
    if (!svgRef.current || !data || !data.tasks) return;

    const margin = { top: 100, right: MARGIN_RIGHT, bottom: 100, left: MARGIN_LEFT };
    const calculatedWidth =
      timelineData.quarters.length * QUARTER_WIDTH +
      TIMELINE_PADDING_LEFT +
      TIMELINE_PADDING_RIGHT +
      margin.left +
      margin.right;
    
    const chartHeight = height - margin.top - margin.bottom;

    // Clear previous chart
    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const g = svg
      .attr('width', calculatedWidth)
      .attr('height', height)
      .attr('viewBox', `0 0 ${calculatedWidth} ${height}`)
      .attr('preserveAspectRatio', 'xMinYMin meet')
      .append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`);

    // Create scale function based on absolute month numbers with fixed 450px per quarter
    const getQuarterForMonth = (absoluteMonth: number): { quarterIndex: number; monthInQuarter: number } => {
      const monthYear = Math.floor((absoluteMonth - 1) / 12);
      const monthNum = ((absoluteMonth - 1) % 12) + 1;
      const quarter = Math.floor((monthNum - 1) / 3);
      
      // Find the quarter index in our quarters array
      const quarterIndex = timelineData.quarters.findIndex(q => {
        const qYear = Math.floor((q.startAbsoluteMonth - 1) / 12);
        const qMonth = ((q.startAbsoluteMonth - 1) % 12) + 1;
        const qQuarter = Math.floor((qMonth - 1) / 3);
        return qYear === monthYear && qQuarter === quarter;
      });
      
      if (quarterIndex === -1) {
        // Fallback: estimate based on position
        const monthOffset = absoluteMonth - timelineData.startAbsoluteMonth;
        const estimatedQuarterIndex = Math.floor(monthOffset / 3);
        const monthInQuarter = (monthOffset % 3) + 1;
        return { quarterIndex: Math.max(0, Math.min(estimatedQuarterIndex, timelineData.quarters.length - 1)), monthInQuarter };
      }
      
      // Calculate position within the quarter (1-3)
      const quarterStartMonth = timelineData.quarters[quarterIndex].startAbsoluteMonth;
      const monthInQuarter = absoluteMonth - quarterStartMonth + 1; // 1-3
      
      return { quarterIndex, monthInQuarter };
    };
    
    const xScale = (absoluteMonth: number): number => {
      const { quarterIndex, monthInQuarter } = getQuarterForMonth(absoluteMonth);
      const positionInQuarter = (monthInQuarter - 1) / 3; // 0 to 1
      return TIMELINE_PADDING_LEFT + quarterIndex * QUARTER_WIDTH + positionInQuarter * QUARTER_WIDTH;
    };

    const yScale = d3.scaleBand()
      .domain(sortedTasks.map(d => d.id))
      .range([0, chartHeight])
      .padding(0.4); // Reduced padding to make boxes bigger

    // Draw year labels (grouped above quarters)
    const yearLabels = g.append('g').attr('class', 'year-labels');
    timelineData.years.forEach(year => {
      const yearQuarters = timelineData.quarters.filter(q => q.year === year);
      if (yearQuarters.length > 0) {
        const firstQuarter = yearQuarters[0];
        const lastQuarter = yearQuarters[yearQuarters.length - 1];
        const yearStartX = xScale(firstQuarter.startAbsoluteMonth - 0.5);
        const yearEndX = xScale(lastQuarter.endAbsoluteMonth + 0.5);
        const yearWidth = yearEndX - yearStartX;
        
        yearLabels.append('text')
          .attr('x', yearStartX + yearWidth / 2)
          .attr('y', -60)
          .attr('text-anchor', 'middle')
          .attr('font-size', '16px')
          .attr('font-weight', 'bold')
          .attr('fill', '#333')
          .text(year.toString());
      }
    });

    // Draw quarter background rectangles
    const quarterBackgrounds = g.append('g').attr('class', 'quarter-backgrounds');
    timelineData.quarters.forEach(quarter => {
      const quarterStartX = xScale(quarter.startAbsoluteMonth - 0.5);
      const quarterEndX = xScale(quarter.endAbsoluteMonth + 0.5);
      const quarterWidth = quarterEndX - quarterStartX;
      
      // Alternate background color for quarters
      const isEven = quarter.quarter % 2 === 0;
      quarterBackgrounds.append('rect')
        .attr('x', quarterStartX)
        .attr('y', -40)
        .attr('width', quarterWidth)
        .attr('height', 60)
        .attr('fill', isEven ? '#F9FAFB' : '#FFFFFF')
        .attr('opacity', 0.5)
        .style('pointer-events', 'none')
        .style('stroke', '#d1d5db')
        .style('stroke-width', '1px');
    });

    // Draw month grid lines
    const monthLines = g.append('g').attr('class', 'month-lines');
    for (let absMonth = timelineData.startAbsoluteMonth; absMonth <= timelineData.endAbsoluteMonth; absMonth++) {
      monthLines.append('line')
        .attr('x1', xScale(absMonth - 0.5))
        .attr('x2', xScale(absMonth - 0.5))
        .attr('y1', 0)
        .attr('y2', chartHeight)
        .attr('stroke', '#e0e0e0')
        .attr('stroke-width', 1)
        .attr('stroke-dasharray', '2,2');
    }

    // Draw quarter grid lines
    const quarterLines = g.append('g').attr('class', 'quarter-lines');
    timelineData.quarters.forEach(quarter => {
      quarterLines.append('line')
        .attr('x1', xScale(quarter.startAbsoluteMonth - 0.5))
        .attr('x2', xScale(quarter.startAbsoluteMonth - 0.5))
        .attr('y1', 0)
        .attr('y2', chartHeight)
        .attr('stroke', '#e0e0e0')
        .attr('stroke-width', 1)
        .attr('stroke-dasharray', 'none');
    });
    
    // Add right border line
    quarterLines.append('line')
      .attr('x1', xScale(timelineData.endAbsoluteMonth + 0.5))
      .attr('x2', xScale(timelineData.endAbsoluteMonth + 0.5))
      .attr('y1', 0)
      .attr('y2', chartHeight)
      .attr('stroke', '#e0e0e0')
      .attr('stroke-width', 1)
      .attr('stroke-dasharray', 'none');

    // Quarter labels with year
    const quarterLabels = g.append('g').attr('class', 'quarter-labels');
    timelineData.quarters.forEach(quarter => {
      const quarterStartX = xScale(quarter.startAbsoluteMonth - 0.5);
      const quarterEndX = xScale(quarter.endAbsoluteMonth + 0.5);
      const quarterCenter = quarterStartX + (quarterEndX - quarterStartX) / 2;
      
      // Quarter label
      quarterLabels.append('text')
        .attr('x', quarterCenter)
        .attr('y', -20)
        .attr('text-anchor', 'middle')
        .attr('font-size', '14px')
        .attr('font-weight', 'bold')
        .attr('fill', '#666')
        .text(`Q${quarter.quarter} ${quarter.year}`);
      
      // Month labels for each quarter
      if (quarter.months && quarter.months.length > 0) {
        quarter.months.forEach((monthData, index) => {
          const monthStartX = xScale(monthData.absoluteMonth - 0.5);
          const monthEndX = index < quarter.months.length - 1 
            ? xScale(quarter.months[index + 1].absoluteMonth - 0.5)
            : xScale(quarter.endAbsoluteMonth + 0.5);
          const monthCenter = monthStartX + (monthEndX - monthStartX) / 2;
          
          quarterLabels.append('text')
            .attr('x', monthCenter)
            .attr('y', -5)
            .attr('text-anchor', 'middle')
            .attr('font-size', '11px')
            .attr('fill', '#888')
            .text(getMonthName(monthData.month));
        });
      }
    });

    // Draw task boxes
    const taskBoxes = g.append('g').attr('class', 'task-boxes');
    
    taskBoxes.selectAll('g')
      .data(sortedTasks)
      .enter()
      .append('g')
      .attr('class', 'task')
      .each(function(d) {
        const taskGroup = d3.select(this);
        
        // Handle projects without end months - extend to end of last displayed quarter
        let taskEndMonth = d.endMonth;
        if (!taskEndMonth) {
          // Use the end of the last displayed quarter
          taskEndMonth = timelineData.endAbsoluteMonth;
        }
        
        const taskWidth = xScale(taskEndMonth + 0.5) - xScale(d.startMonth - 0.5);
        const taskHeight = yScale.bandwidth();
        const taskX = xScale(d.startMonth - 0.5);
        const taskY = yScale(d.id)!;

        // console.log(`Rendering task ${d.name}:`, {
        //   startMonth: d.startMonth,
        //   endMonth: d.endMonth,
        //   taskWidth,
        //   taskHeight,
        //   taskX,
        //   taskY,
        //   yScaleBandwidth: yScale.bandwidth(),
        //   xScaleDomain: xScale.domain(),
        //   xScaleRange: xScale.range()
        // });

        // Helper function to get text width
        const getTextWidth = (text: string, fontSize: string, fontWeight: string) => {
          const canvas = document.createElement('canvas');
          const context = canvas.getContext('2d');
          if (context) {
            context.font = `${fontWeight} ${fontSize} Arial`;
            return context.measureText(text).width;
          }
          return text.length * 7; // Adjusted fallback estimation
        };

        // Task box with clean, modern styling - no colored dots, just clean design
        const taskRect = taskGroup.append('rect')
          .attr('x', taskX + 2)
          .attr('y', taskY + 2)
          .attr('width', taskWidth - 4)
          .attr('height', taskHeight - 4)
          .attr('rx', 6)
          .attr('ry', 6)
          .attr('fill', '#f8f9fa')
          .attr('stroke', '#dee2e6')
          .attr('stroke-width', 1.5)
          .style('filter', 'drop-shadow(0 2px 8px rgba(0,0,0,0.08))')
          .style('cursor', interactive ? 'pointer' : 'default')
          .style('opacity', 1);

        // Debug: Add a subtle border to show text boundaries (remove this in production)
        // taskGroup.append('rect')
        //   .attr('x', taskX + 6)
        //   .attr('y', taskY + 6)
        //   .attr('width', taskWidth - 12)
        //   .attr('height', taskHeight - 12)
        //   .attr('fill', 'none')
        //   .attr('stroke', '#ff0000')
        //   .attr('stroke-width', 0.5)
        //   .attr('stroke-dasharray', '2,2')
        //   .style('opacity', 0.3);

        // Thin colored line on top of the box (below the border) - this is the main color indicator
        // Ensure the line stays within the box boundaries
        taskGroup.append('rect')
          .attr('x', taskX + 2)
          .attr('y', taskY + 2)
          .attr('width', taskWidth - 4)
          .attr('height', 3)
          .attr('rx', 1.5)
          .attr('ry', 1.5)
          .attr('fill', d.color);

        // Task label - centered text inside the clean box with proper containment
        const textElement = taskGroup.append('text')
          .attr('x', taskX + taskWidth / 2)
          .attr('y', taskY + taskHeight / 2 + 3)
          .attr('text-anchor', 'middle')
          .attr('font-size', '10px')
          .attr('font-weight', '600')
          .attr('fill', '#2c3e50')
          .style('pointer-events', 'none');

        // Handle long text by wrapping to multiple lines with better containment
        const wrapText = (text: string, width: number) => {
          const words = text.split(' ');
          const lines: string[] = [];
          
          // If text is very long, break it more aggressively
          if (text.length > 20) {
            // Break into smaller chunks
            for (let i = 0; i < text.length; i += 15) {
              const chunk = text.slice(i, i + 15);
              if (chunk.trim()) {
                lines.push(chunk.trim());
              }
            }
            return lines;
          }
          
          let currentLine = words[0];

          for (let i = 1; i < words.length; i++) {
            const word = words[i];
            const testLine = currentLine + ' ' + word;
            const testWidth = getTextWidth(testLine, '10px', '600');
            
            if (testWidth > width - 32) { // Increased margin for better containment
              lines.push(currentLine);
              currentLine = word;
            } else {
              currentLine = testLine;
            }
          }
          lines.push(currentLine);
          return lines;
        };

        // Check if text needs to be wrapped
        const textWidth = getTextWidth(d.name, '10px', '600');
        if (textWidth > taskWidth - 32) {
          // Text is too long, wrap to multiple lines
          const lines = wrapText(d.name, taskWidth);
          
          // Remove the single text element
          textElement.remove();
          
          // Add multiple lines of text with better containment and smaller font
          lines.forEach((line, index) => {
            const lineY = taskY + taskHeight / 2 + (index - (lines.length - 1) / 2) * 14;
            taskGroup.append('text')
              .attr('x', taskX + taskWidth / 2)
              .attr('y', lineY)
              .attr('text-anchor', 'middle')
              .attr('font-size', '9px')
              .attr('font-weight', '600')
              .attr('fill', '#2c3e50')
              .style('pointer-events', 'none')
              .text(line);
          });
        } else {
          // Text fits in one line
          textElement.text(d.name);
        }

        // Interactive events
        if (interactive) {
          taskRect
            .on('mouseover', function(event) {
              if (showTooltips) {
                const startDate = getMonthFromAbsolute(d.startMonth);
                // Handle projects without end months
                const taskEndMonth = d.endMonth || timelineData.endAbsoluteMonth;
                const endDate = getMonthFromAbsolute(taskEndMonth);
                const startMonthName = getMonthName(startDate.month);
                const endMonthName = getMonthName(endDate.month);
                
                // Get dependency information
                let dependencyInfo = '';
                if (d.dependencies && d.dependencies.length > 0) {
                  const dependencyNames = d.dependencies.map(depId => {
                    const depTask = data.tasks.find(t => t.id === depId);
                    return depTask ? depTask.name : depId;
                  });
                  dependencyInfo = `<br/><strong>Dependencies:</strong> ${dependencyNames.join(', ')}`;
                }
                
                const tooltipContent = `
                  <div style="font-family: Arial, sans-serif; font-size: 12px; width: 250px;">
                    <div style="font-weight: bold; color: #1f2937; margin-bottom: 6px; font-size: 14px;">${d.name}</div>
                    <div style="color: #6b7280; margin-bottom: 4px;"><strong>Timeline:</strong> ${startMonthName} ${startDate.year} - ${endMonthName} ${endDate.year}</div>
                    ${dependencyInfo}
                  </div>
                `;
                
                // Calculate tooltip position with boundary detection
                const tooltipWidth = 250; // Fixed tooltip width
                const tooltipHeight = 80; // Fixed tooltip height (smaller without description)
                const padding = 20; // Padding from screen edges
                
                let tooltipX = event.clientX + 10;
                let tooltipY = event.clientY - 10;
                
                // Check right boundary
                if (tooltipX + tooltipWidth + padding > window.innerWidth) {
                  tooltipX = event.clientX - tooltipWidth - 10;
                }
                
                // Check left boundary
                if (tooltipX < padding) {
                  tooltipX = padding;
                }
                
                // Check top boundary
                if (tooltipY - tooltipHeight < padding) {
                  tooltipY = event.clientY + 20; // Show below cursor instead
                }
                
                // Check bottom boundary
                if (tooltipY + tooltipHeight + padding > window.innerHeight) {
                  tooltipY = window.innerHeight - tooltipHeight - padding;
                }
                
                setTooltip({
                  show: true,
                  content: tooltipContent,
                  x: tooltipX,
                  y: tooltipY
                });
              }
              // Clean hover effect - subtle border change and shadow
              d3.select(this)
                .style('filter', 'drop-shadow(0 4px 12px rgba(0,0,0,0.12))')
                .style('stroke', '#adb5bd')
                .style('stroke-width', 2);
            })
            .on('mouseout', function() {
              setTooltip({ show: false, content: '', x: 0, y: 0 });
              // Reset hover effect
              d3.select(this)
                .style('filter', 'drop-shadow(0 2px 8px rgba(0,0,0,0.08))')
                .style('stroke', '#dee2e6')
                .style('stroke-width', 1.5);
            })
            .on('click', () => {
              if (onTaskClick) onTaskClick(d);
            });
        }
      });

    // Draw dependency lines
    const dependencyLines = g.append('g').attr('class', 'dependency-lines');
    

    
    // Create a map of task positions for easy lookup
    const taskPositions = new Map();
    sortedTasks.forEach(task => {
      // Handle projects without end months
      const taskEndMonth = task.endMonth || timelineData.endAbsoluteMonth;
      
      const taskX = xScale(task.startMonth - 0.5);
      const taskY = yScale(task.id)!;
      const taskWidth = xScale(taskEndMonth + 0.5) - xScale(task.startMonth - 0.5);
      const taskHeight = yScale.bandwidth();
      
      taskPositions.set(task.id, {
        x: taskX,
        y: taskY,
        width: taskWidth,
        height: taskHeight,
        centerX: taskX + taskWidth / 2,
        centerY: taskY + taskHeight / 2,
        endX: taskX + taskWidth,
        startX: taskX
      });
    });

    // Draw dependency lines
    sortedTasks.forEach(task => {
      if (task.dependencies && task.dependencies.length > 0) {
        task.dependencies.forEach(depId => {
          const sourceTask = sortedTasks.find(t => t.id === depId);
          if (sourceTask && taskPositions.has(depId) && taskPositions.has(task.id)) {
            const source = taskPositions.get(depId);
            const target = taskPositions.get(task.id);
            
            // Connect from right-mid of source to left-mid of target (horizontal flow)
            const startX = source.endX;      // Right edge of source
            const startY = source.centerY;   // Vertical center of source
            const endX = target.startX;      // Left edge of target
            const endY = target.centerY;    // Vertical center of target
            
            // Control points for horizontal right-to-left curve
            const distance = Math.abs(endX - startX);
            
            // Control 1: extend horizontally from source (right)
            const controlPoint1X = startX + Math.max(distance * 0.3, 40);
            const controlPoint1Y = startY;
            
            // Control 2: extend from target (left), curve toward source
            const controlPoint2X = endX - Math.max(distance * 0.3, 40);
            const controlPoint2Y = endY;
            
            // Create curved path
            const pathData = `M ${startX} ${startY} C ${controlPoint1X} ${controlPoint1Y}, ${controlPoint2X} ${controlPoint2Y}, ${endX} ${endY}`;
            
            // Draw the dependency line with clean, simple styling
            dependencyLines.append('path')
              .attr('d', pathData)
              .attr('stroke', '#ff6b6b') // Red color for dependency lines
              .attr('stroke-width', 2)
              .attr('fill', 'none')
              .style('opacity', 0.8)
              .style('cursor', 'pointer')
              .on('mouseover', function() {
                d3.select(this)
                  .style('opacity', 1)
                  .style('stroke-width', 3);
              })
              .on('mouseout', function() {
                d3.select(this)
                  .style('opacity', 0.8)
                  .style('stroke-width', 2);
              });
            
            // Add dots at both ends
            dependencyLines.append('circle')
              .attr('cx', startX)
              .attr('cy', startY)
              .attr('r', 3)
              .attr('fill', '#ff6b6b')
              .style('opacity', 0.8)
              .on('mouseover', function() {
                d3.select(this)
                  .style('opacity', 1)
                  .attr('r', 4);
              })
              .on('mouseout', function() {
                d3.select(this)
                  .style('opacity', 0.8)
                  .attr('r', 3);
              });
            
            dependencyLines.append('circle')
              .attr('cx', endX)
              .attr('cy', endY)
              .attr('r', 3)
              .attr('fill', '#ff6b6b')
              .style('opacity', 0.8)
              .on('mouseover', function() {
                d3.select(this)
                  .style('opacity', 1)
                  .attr('r', 4);
              })
              .on('mouseout', function() {
                d3.select(this)
                  .style('opacity', 0.8)
                  .attr('r', 3);
              });
          }
        });
      }
    });



    // Chart title - REMOVED to avoid duplicate headers
    // g.append('text')
    //   .attr('x', 0)
    //   .attr('y', -50)
    //   .attr('font-size', '20px')
    //   .attr('font-weight', 'bold')
    //   .attr('fill', '#333')
    //   .text('Transformation Project Roadmap - Full Year (Q1-Q4)');

    // Legend - REMOVED as requested
    // if (showLegends) {
    //   const legend = g.append('g').attr('class', 'legend').attr('transform', `translate(${chartWidth + 30}, 0)`);
    //   
    //   // Status legend
    //   const statusLegend = legend.append('g').attr('transform', 'translate(0, 0)');
    //   statusLegend.append('text').attr('x', 0).attr('y', 0).attr('font-size', '12px').attr('font-weight', 'bold').text('Status:');
    //   
    //   const statuses = [
    //     { color: '#e0e0e0', label: 'Not Started' },
    //     { color: '#ffa726', label: 'In Progress' },
    //     { color: '#4caf50', label: 'Completed' },
    //     { color: '#f44336', label: 'Delayed' }
    //   ];

    //   statuses.forEach((status, i) => {
    //     const legendItem = statusLegend.append('g').attr('transform', `translate(0, ${(i + 1) * 20})`);
    //     legendItem.append('circle').attr('cx', 0).attr('cy', 0).attr('r', 4).attr('fill', status.color);
    //     legendItem.append('text').attr('x', 10).attr('y', 4).attr('font-size', '10px').text(status.label);
    //   });

    //   // Priority legend
    //   const priorityLegend = legend.append('g').attr('transform', 'translate(0, 120)');
    //   priorityLegend.append('text').attr('x', 0).attr('y', 0).attr('font-size', '12px').attr('font-weight', 'bold').text('Priority:');
    //   
    //   const priorities = [
    //     { color: '#4caf50', label: 'Low' },
    //     { color: '#ffa726', label: 'Medium' },
    //     { color: '#ff6b6b', label: 'High' },
    //     { color: '#9c27b0', label: 'Critical' }
    //   ];

    //   priorities.forEach((priority, i) => {
    //     const legendItem = priorityLegend.append('g').attr('transform', `translate(0, ${(i + 1) * 20})`);
    //     legendItem.append('text').attr('x', 10).attr('y', 0).attr('font-size', '10px').text(priority.label);
    //   });
    // }

  }, [width, height, data, sortedTasks, timelineData, showTooltips, showLegends, interactive, onTaskClick, onMilestoneClick]);

  // Calculate if we need scrolling (must match useEffect formula)
  const calculatedWidth =
    timelineData.quarters.length * QUARTER_WIDTH +
    TIMELINE_PADDING_LEFT +
    TIMELINE_PADDING_RIGHT +
    MARGIN_LEFT +
    MARGIN_RIGHT;
  const needsScrolling = calculatedWidth > width;
  const fitScale = Math.min(1, width / calculatedWidth);
  const effectiveScale = zoomScale;
  const renderedWidth = calculatedWidth * effectiveScale;
  const renderedHeight = height * effectiveScale;

  const clampScale = (nextScale: number) => Math.max(0.5, Math.min(2, nextScale));
  const zoomIn = () => setZoomScale((prev) => clampScale(prev * 1.15));
  const zoomOut = () => setZoomScale((prev) => clampScale(prev / 1.15));
  const fitToView = () => setZoomScale(fitScale);

  useEffect(() => {
    setZoomScale(1);
  }, [data, width, height]);

  // Show loading state if no data
  if (!data || !data.tasks || data.tasks.length === 0) {
    return (
      <div className="transformation-roadmap-chart relative">
        <div className="flex items-center justify-center h-64 text-gray-500">
          <div className="text-center">
            <div className="text-lg font-medium mb-2">No Project Data Available</div>
            <div className="text-sm">Add projects to see the transformation roadmap</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="transformation-roadmap-chart relative space-y-4">
      {interactive && (
        <div className="flex flex-wrap gap-3">
          <button onClick={zoomIn} className="rounded-2xl px-4 py-2 bg-white border border-gray-300 text-gray-700">
            Zoom In
          </button>
          <button onClick={zoomOut} className="rounded-2xl px-4 py-2 bg-white border border-gray-300 text-gray-700">
            Zoom Out
          </button>
          <button onClick={fitToView} className="rounded-2xl px-4 py-2 bg-white border border-gray-300 text-gray-700">
            Fit to View
          </button>
        </div>
      )}

      <div 
        ref={containerRef}
        className="relative"
        style={{
          overflowX: needsScrolling || effectiveScale > 1 ? 'auto' : 'visible',
          overflowY: 'visible'
        }}
      >
        <svg
          ref={svgRef}
          style={{
            width: `${renderedWidth}px`,
            height: `${renderedHeight}px`,
            minWidth: effectiveScale < 1 ? `${renderedWidth}px` : undefined,
            display: 'block',
          }}
        ></svg>
      </div>
      

      
      {/* Enhanced Tooltip */}
      {tooltip.show && (
        <div
          className="fixed pointer-events-none z-[9999]"
          style={{
            left: tooltip.x,
            top: tooltip.y,
            width: '250px'
          }}
        >
          <div
            className="bg-white border border-gray-200 rounded-xl shadow-lg p-4"
            style={{
              boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
              borderRadius: '12px'
            }}
            dangerouslySetInnerHTML={{ __html: tooltip.content }}
          />
        </div>
      )}
    </div>
  );
};

export default TransformationRoadmapChart;
